import React, { useState, useEffect, useMemo } from 'react';
import { LABEL_TEXT, INPUT_BASE } from '../styles/classNames';

/**
 * Современный компонент выбора даты и времени с календарем
 * Оптимизирован для близких дат (недели/месяцы вперед)
 */
export function DateTimePicker({ value, onChange, disabled = false }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState({ hour: '12', minute: '00' });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [validationError, setValidationError] = useState('');

  // Parse existing value on mount
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setSelectedTime({
          hour: String(date.getHours()).padStart(2, '0'),
          minute: String(date.getMinutes()).padStart(2, '0')
        });
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
  }, [value]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday = 0
    
    const days = [];
    
    // Empty cells before first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, isOtherMonth: true });
    }
    
    // Days of current month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      
      const isPast = date < today;
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDate && 
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate();
      
      days.push({
        date,
        day,
        isPast,
        isToday,
        isSelected,
        isOtherMonth: false
      });
    }
    
    return days;
  }, [currentMonth, selectedDate]);

  const handleDateSelect = (dayInfo) => {
    if (dayInfo.isPast || dayInfo.isOtherMonth || !dayInfo.date) return;
    
    setSelectedDate(dayInfo.date);
    setValidationError('');
  };

  const handleTimeChange = (field, value) => {
    setSelectedTime(prev => ({ ...prev, [field]: value }));
    setValidationError('');
  };

  const handleApply = () => {
    if (!selectedDate) {
      setValidationError('Выберите дату');
      return;
    }
    
    const hour = parseInt(selectedTime.hour);
    const minute = parseInt(selectedTime.minute);
    
    if (isNaN(hour) || hour < 0 || hour > 23) {
      setValidationError('Некорректный час (0-23)');
      return;
    }
    
    if (isNaN(minute) || minute < 0 || minute > 59) {
      setValidationError('Некорректные минуты (0-59)');
      return;
    }
    
    const finalDate = new Date(selectedDate);
    finalDate.setHours(hour, minute, 0, 0);
    
    // Validate future date
    if (finalDate <= new Date()) {
      setValidationError('Дата и время должны быть в будущем');
      return;
    }
    
    onChange(finalDate.toISOString());
    setShowCalendar(false);
    setValidationError('');
  };

  const handleClear = () => {
    setSelectedDate(null);
    setSelectedTime({ hour: '12', minute: '00' });
    onChange(null);
    setShowCalendar(false);
    setValidationError('');
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const formatDisplayValue = () => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const monthName = currentMonth.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });

  return (
    <div className="relative">
      {/* Input field */}
      <div 
        className={`${INPUT_BASE} cursor-pointer flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setShowCalendar(!showCalendar)}
      >
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
          {formatDisplayValue() || 'Выберите дату и время'}
        </span>
        <span className="text-xl">📅</span>
      </div>

      {/* Calendar dropdown */}
      {showCalendar && !disabled && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-4 w-full md:w-96">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goToPreviousMonth}
              className="px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              type="button"
            >
              ‹
            </button>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm capitalize">{monthName}</span>
              <button
                onClick={goToToday}
                className="px-2 py-1 text-xs rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                type="button"
              >
                Сегодня
              </button>
            </div>
            <button
              onClick={goToNextMonth}
              className="px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              type="button"
            >
              ›
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {calendarDays.map((dayInfo, idx) => {
              if (!dayInfo.date) {
                return <div key={idx} className="aspect-square" />;
              }
              
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleDateSelect(dayInfo)}
                  disabled={dayInfo.isPast}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition-all
                    ${dayInfo.isPast 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                    }
                    ${dayInfo.isToday && !dayInfo.isSelected
                      ? 'bg-gray-100 dark:bg-gray-700'
                      : ''
                    }
                    ${dayInfo.isSelected
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'text-gray-700 dark:text-gray-300'
                    }
                  `}
                >
                  {dayInfo.day}
                </button>
              );
            })}
          </div>

          {/* Time picker */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
            <div className={LABEL_TEXT}>Время (24ч формат)</div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={selectedTime.hour}
                  onChange={(e) => handleTimeChange('hour', e.target.value.padStart(2, '0'))}
                  className={`${INPUT_BASE} text-center`}
                  placeholder="ЧЧ"
                />
              </div>
              <span className="text-xl">:</span>
              <div className="flex-1">
                <select
                  value={selectedTime.minute}
                  onChange={(e) => handleTimeChange('minute', e.target.value)}
                  className={INPUT_BASE}
                >
                  <option value="00">00</option>
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                </select>
              </div>
            </div>
          </div>

          {/* Validation error */}
          {validationError && (
            <div className="mb-4 text-sm text-red-600 dark:text-red-400">
              {validationError}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleClear}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              type="button"
            >
              Очистить
            </button>
            <button
              onClick={handleApply}
              className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors text-sm font-medium"
              type="button"
            >
              Применить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
